import { isPlayerEvent, PlayerEvents } from "../player/player-events";
import { ReactiveElement, ReactiveState } from "@nrk/reactive-element";
import { DimensionsMixin } from "@nrk/element-dimensions-mixin";
import { getElementOfType } from "../utils/dom";
import { computeCuePosition } from "../utils/cue-position";
import { PlayerActions } from "../player/player-actions";
class SubtitlesElement extends DimensionsMixin(ReactiveElement) {
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      subtitleState: ReactiveState,
      fullscreenState: Object,
      pipState: Object,
      remotePlaybackState: Object
    };
  }
  playerAspectRatio;
  videoAspectRatio;
  subtitleState;
  fullscreenState;
  pipState;
  remotePlaybackState;
  nativeRenderingEnabled;
  cueConfig = {};
  action = PlayerActions.Speak;
  player;
  value;
  activeCuePositions = [];
  _unshadowProperties;
  connectedCallback() {
    super.connectedCallback();
    this.player = getElementOfType(this);
    this.player.addEventListener(PlayerEvents.QualityChange, this);
    this._unshadowProperties = this.player.shadowProperties(["subtitleState"], this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.player?.removeEventListener(PlayerEvents.QualityChange, this);
    this._unshadowProperties?.();
    this.player = null;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (!this.isConnected || !this.player) {
      return;
    }
    if (changedProperties.has("subtitleState") && this.subtitleState) {
      const cues = this.subtitleState.activeCues;
      const stripTags = (s) => s.replace(/<[/\w][^<]*>/g, "");
      const text = cues.map((cue) => stripTags(cue.text)).join(" ");
      if (text) {
        this.value = text;
        this.dispatchEvent(new Event("change", { cancelable: true, bubbles: true }));
      }
      this.activeCuePositions = cues.map((cue) => computeCuePosition(cue, this.cueConfig));
    }
    if (changedProperties.has("fullscreenState")) {
      const useNativeRendering = this.fullscreenState?.isActive && this.fullscreenState.isNativeUI;
      if (this.nativeRenderingEnabled !== useNativeRendering) {
        this.nativeRenderingEnabled = useNativeRendering;
        if (useNativeRendering) {
          this.player.activeSession?.enableNativeSubtitlesDisplay();
        } else {
          this.player.activeSession?.disableNativeSubtitlesDisplay();
        }
      }
    }
    if (changedProperties.has("dimensions")) {
      this.playerAspectRatio = this.player.clientWidth / this.player.clientHeight;
      this.updateRenderingArea();
    }
  }
  handleEvent(event) {
    if (isPlayerEvent(event, PlayerEvents.QualityChange)) {
      if (!this.videoAspectRatio && this.player.activeSession?.getCurrentQuality()) {
        const quality = this.player.activeSession.getCurrentQuality();
        if (quality?.width && quality.height) {
          this.playerAspectRatio = this.player.clientWidth / this.player.clientHeight;
          this.videoAspectRatio = quality.width / quality.height;
          this.updateRenderingArea();
        }
      }
    }
  }
  isRenderingEnabled() {
    return !this.nativeRenderingEnabled && !this.pipState?.isActive && !this.remotePlaybackState?.isActive;
  }
  updateRenderingArea() {
    const blackBars = this.getBlackBars();
    this.style.top = blackBars.top ?? "0px";
    this.style.right = blackBars.right ?? "0px";
    this.style.bottom = blackBars.bottom ?? "0px";
    this.style.left = blackBars.left ?? "0px";
  }
  getBlackBars() {
    const playerAspectRatio = this.playerAspectRatio;
    const videoAspectRatio = this.videoAspectRatio;
    if (playerAspectRatio === void 0 || videoAspectRatio === void 0) {
      return {};
    }
    if (playerAspectRatio === videoAspectRatio || this.getObjectFit() !== "contain") {
      return {};
    }
    if (playerAspectRatio > videoAspectRatio) {
      const blackBarSize = (1 - videoAspectRatio / playerAspectRatio) / 2 * 100;
      return {
        right: `${blackBarSize}%`,
        left: `${blackBarSize}%`
      };
    } else {
      const blackBarSize = (1 - playerAspectRatio / videoAspectRatio) / 2 * 100;
      return {
        top: `${blackBarSize}%`,
        bottom: `${blackBarSize}%`
      };
    }
  }
  getObjectFit() {
    const video = this.player?.querySelector("video");
    return video ? getComputedStyle(video).objectFit : "contain";
  }
}
export {
  SubtitlesElement
};
